import React, { useState } from "react";
import { Dialog, DialogContent } from "@material-tailwind/react";
import { X } from "lucide-react";
import { getMediaUrl } from "../utils/media";

const ImageCarousel = ({
  images,
  isEditing,
  newImages,
  handleImageRemove,
  isProgressSection = false, // New prop to indicate if it's the progress section
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const hasImages = images && Array.isArray(images) && images?.length > 0;
  const hasNewImages = isEditing && newImages && newImages.length > 0;

  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) => {
      const imageSource = hasNewImages ? newImages : images;
      return prevIndex > 0 ? prevIndex - 1 : imageSource.length - 1;
    });
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) => {
      const imageSource = hasNewImages ? newImages : images;
      return prevIndex < imageSource.length - 1 ? prevIndex + 1 : 0;
    });
  };

  const getImageSource = () => {
    if (hasNewImages) {
      return newImages[currentImageIndex].base64;
    }
    if (hasImages) {
      return getMediaUrl(images[currentImageIndex]);
    }
    return null;
  };

  const getFormattedImageKey = () => {
    let key = "";
    if (hasNewImages) {
      key = newImages[currentImageIndex].name;
    }
    if (hasImages) {
      key = images[currentImageIndex]?.key;
    }

    if (!key) return null;

    key = key.replace(/_/g, " ");

    const parts = key.split(".");
    if (parts.length > 1) {
      parts.pop();
      if (
        parts.length > 1 &&
        parts[parts.length - 1].toLowerCase() === "jpeg"
      ) {
        parts.pop();
      }
      key = parts.join(".");
    }
    return key;
  };

  if (!hasImages && !hasNewImages) {
    return (
      <div className="flex justify-center items-center p-8">
        <p className="text-gray-500">No Images Available</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="relative flex justify-center items-center">
        {/* Previous Image (Stacked) */}
        {((images && images.length > 1) || (newImages && newImages.length > 1)) && (
          <div className="absolute transform -translate-x-40 scale-95 opacity-10 z-0">
            <img
              src={getMediaUrl(
                images[
                  (currentImageIndex - 1 + images.length) % images.length
                ]
              )}
              alt="Previous"
              className="w-[350px] h-[350px] object-cover rounded-lg shadow-lg"
              style={{ aspectRatio: "1 / 1" }}
            />
          </div>
        )}

        {/* Current Image */}
        <div className="relative z-10 transition-all">
          <img
            src={getImageSource()}
            alt={`Progress ${currentImageIndex + 1}`}
            className={`w-[350px] h-[350px] object-cover rounded-lg shadow-lg transition-all duration-300 cursor-pointer`}
            style={{ aspectRatio: "1 / 1" }}
            onClick={() => setIsDialogOpen(true)}
          />

          <Dialog
            open={isDialogOpen}
            handler={setIsDialogOpen}
            size="xl"
            className="z-50"
          >
            <DialogContent className="bg-white">
              <div className="flex justify-end">
                <button
                  className="bg-white/80 hover:bg-white text-gray-600 hover:text-gray-900 rounded-full p-2 transition-colors duration-200 shadow-lg"
                  onClick={() => setIsDialogOpen(false)}
                >
                  <X className="size-5" />
                </button>
              </div>
              <img
                alt="nature"
                src={getImageSource()}
                className="h-[48rem] w-full rounded-lg object-contain object-center"
              />
            </DialogContent>
          </Dialog>
          <p className="text-sm font-medium text-gray-700 mt-1 text-center">
            {getFormattedImageKey()}
          </p>
        </div>

        {/* Next Image (Stacked) */}
        {((images && images.length > 1) || (newImages && newImages.length > 1)) && (
          <div className="absolute transform translate-x-40 scale-95 opacity-10 z-0">
            <img
              src={getMediaUrl(
                images[(currentImageIndex + 1) % images.length]
              )}
              alt="Next"
              className="w-[350px] h-[350px] object-cover rounded-lg shadow-lg"
              style={{ aspectRatio: "1 / 1" }}
            />
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="absolute top-1/2 transform -translate-y-1/2 flex justify-between w-full px-2 z-20">
          <button
            onClick={handlePrevImage}
            className="bg-white/80 hover:bg-white text-gray-600 hover:text-gray-900 rounded-full p-2 transition-colors duration-200 shadow-lg"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.75 19.5L8.25 12l7.5-7.5"
              />
            </svg>
          </button>
          <button
            onClick={handleNextImage}
            className="bg-white/80 hover:bg-white text-gray-600 hover:text-gray-900 rounded-full p-2 transition-colors duration-200 shadow-lg"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8.25 4.5l7.5 7.5-7.5 7.5"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCarousel;